'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, FileText, Truck, Trash2 } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { salesOrders, customers } from '../data/mock-data';
import {
  SALES_ORDER_STATUS_LABELS,
  SALES_ORDER_STATUS_COLORS,
  PAYMENT_STATUS_LABELS,
  DELIVERY_STATUS_LABELS,
} from '../types';
import type { SalesOrder, SalesOrderStatus, PaymentStatus, DeliveryStatus } from '../types';
import { formatCurrency } from '../../../types';

// 주문 등록 모달
function SalesOrderAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [items, setItems] = useState([{ itemName: '', quantity: 1, unitPrice: 0 }]);

  const addItem = () => {
    setItems([...items, { itemName: '', quantity: 1, unitPrice: 0 }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="주문 등록"
      description="새로운 판매 주문을 등록합니다."
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button variant="outline">초안 저장</Button>
          <Button onClick={onClose}>주문 등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* 기본 정보 */}
        <div className="grid grid-cols-2 gap-4">
          <FormField label="주문번호" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="고객" required>
            <FormSelect>
              <option value="">고객을 선택하세요</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="견적서 참조">
            <FormSelect>
              <option value="">선택 안함</option>
              <option value="qtn-001">QTN-2024-001</option>
              <option value="qtn-002">QTN-2024-002</option>
            </FormSelect>
          </FormField>
          <FormField label="배송 요청일">
            <FormInput type="date" />
          </FormField>
        </div>

        {/* 품목 목록 */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">주문 품목</h3>
            <Button variant="outline" size="sm" onClick={addItem}>
              <Plus className="w-4 h-4 mr-1" />
              품목 추가
            </Button>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">품목명</th>
                  <th className="text-right p-3 font-medium w-24">수량</th>
                  <th className="text-right p-3 font-medium w-32">단가</th>
                  <th className="text-right p-3 font-medium w-32">금액</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-2">
                      <FormSelect>
                        <option value="">품목을 선택하세요</option>
                        <option value="box-a">골판지 박스 A형</option>
                        <option value="box-b">골판지 박스 B형</option>
                        <option value="box-c">친환경 박스</option>
                        <option value="box-d">냉동 박스</option>
                      </FormSelect>
                    </td>
                    <td className="p-2">
                      <FormInput type="number" min={1} defaultValue={1} className="text-right" />
                    </td>
                    <td className="p-2">
                      <FormInput type="number" min={0} defaultValue={0} className="text-right" />
                    </td>
                    <td className="p-2 text-right font-medium">
                      ₩0
                    </td>
                    <td className="p-2">
                      <button
                        onClick={() => removeItem(index)}
                        className="p-1 rounded hover:bg-accent"
                        disabled={items.length === 1}
                      >
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 금액 정보 */}
        <div className="flex justify-end">
          <div className="w-64 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">소계</span>
              <span>₩0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">부가세 (10%)</span>
              <span>₩0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">할인</span>
              <FormInput type="number" min={0} defaultValue={0} className="w-24 text-right h-8" />
            </div>
            <div className="flex justify-between pt-2 border-t border-border font-bold text-base">
              <span>합계</span>
              <span>₩0</span>
            </div>
          </div>
        </div>

        {/* 배송 정보 */}
        <div className="p-4 bg-muted/30 rounded-lg space-y-4">
          <h3 className="font-medium">배송 정보</h3>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="배송지 주소">
              <FormTextarea rows={2} placeholder="배송지 주소" />
            </FormField>
            <FormField label="배송 메모">
              <FormTextarea rows={2} placeholder="배송 시 요청사항" />
            </FormField>
          </div>
        </div>

        {/* 비고 */}
        <FormField label="비고">
          <FormTextarea rows={2} placeholder="주문 관련 메모를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function SalesOrderListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<SalesOrderStatus | 'all'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return salesOrders.filter((order) => {
      // 검색 필터
      const matchesSearch =
        order.orderNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.customerName.toLowerCase().includes(searchQuery.toLowerCase());

      // 상태 필터
      const matchesStatus =
        selectedStatus === 'all' || order.status === selectedStatus;

      return matchesSearch && matchesStatus;
    });
  }, [searchQuery, selectedStatus]);

  const columns = [
    {
      key: 'orderNo',
      label: '주문번호',
      width: '11%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm">{value as string}</span>
      ),
    },
    {
      key: 'customerName',
      label: '고객',
      width: '14%',
      sortable: true,
      render: (value: unknown) => (
        <span className="truncate block">{value as string}</span>
      ),
    },
    {
      key: 'items',
      label: '품목',
      width: '6%',
      render: (value: unknown) => (
        <span className="whitespace-nowrap">{(value as SalesOrder['items']).length}개</span>
      ),
    },
    {
      key: 'total',
      label: '금액',
      width: '12%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => (
        <span className="font-medium whitespace-nowrap">{formatCurrency(value as number)}</span>
      ),
    },
    {
      key: 'deliveryDate',
      label: '배송일',
      width: '10%',
      sortable: true,
      render: (value: unknown) => (
        <span className="text-sm whitespace-nowrap">
          {value ? new Date(value as string).toLocaleDateString('ko-KR') : '-'}
        </span>
      ),
    },
    {
      key: 'status',
      label: '주문상태',
      width: '9%',
      render: (value: unknown) => {
        const status = value as SalesOrderStatus;
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${SALES_ORDER_STATUS_COLORS[status]}`}>
            {SALES_ORDER_STATUS_LABELS[status]}
          </span>
        );
      },
    },
    {
      key: 'paymentStatus',
      label: '결제',
      width: '8%',
      render: (value: unknown) => {
        const status = value as PaymentStatus;
        const colors: Record<PaymentStatus, string> = {
          unpaid: 'text-red-500',
          partial: 'text-yellow-500',
          paid: 'text-green-500',
          overdue: 'text-red-600 font-medium',
        };
        return (
          <span className={`text-xs whitespace-nowrap ${colors[status]}`}>
            {PAYMENT_STATUS_LABELS[status]}
          </span>
        );
      },
    },
    {
      key: 'deliveryStatus',
      label: '배송',
      width: '8%',
      render: (value: unknown) => {
        const status = value as DeliveryStatus;
        const colors: Record<DeliveryStatus, string> = {
          not_delivered: 'text-gray-500',
          partial: 'text-yellow-500',
          delivered: 'text-green-500',
        };
        return (
          <span className={`text-xs whitespace-nowrap ${colors[status]}`}>
            {DELIVERY_STATUS_LABELS[status]}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '10%',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="상세보기">
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="배송처리">
            <Truck className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="인쇄">
            <FileText className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Record<SalesOrderStatus, number> = {
      draft: 0,
      submitted: 0,
      to_deliver: 0,
      to_bill: 0,
      completed: 0,
      cancelled: 0,
    };
    salesOrders.forEach((o) => {
      stats[o.status]++;
    });
    return stats;
  }, []);

  // 요약 정보
  const totalAmount = salesOrders.reduce((sum, o) => sum + o.total, 0);
  const pendingCount = salesOrders.filter(
    (o) => o.status === 'submitted' || o.status === 'to_deliver'
  ).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="판매 주문"
        description="판매 주문을 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            주문 등록
          </button>
        }
      />

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">총 주문</div>
          <div className="text-2xl font-bold mt-1">{salesOrders.length}건</div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">총 주문금액</div>
          <div className="text-2xl font-bold mt-1">{formatCurrency(totalAmount)}</div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">처리 대기</div>
          <div className="text-2xl font-bold mt-1 text-yellow-500">{pendingCount}건</div>
        </div>
      </div>

      {/* 상태별 필터 버튼 */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedStatus('all')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            selectedStatus === 'all'
              ? 'bg-primary text-primary-foreground'
              : 'bg-card border border-border hover:bg-accent'
          }`}
        >
          전체 ({salesOrders.length})
        </button>
        {(Object.keys(SALES_ORDER_STATUS_LABELS) as SalesOrderStatus[]).map((status) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(status)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              selectedStatus === status
                ? 'bg-primary text-primary-foreground'
                : 'bg-card border border-border hover:bg-accent'
            }`}
          >
            {SALES_ORDER_STATUS_LABELS[status]} ({statusStats[status]})
          </button>
        ))}
      </div>

      {/* 검색 */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="주문번호, 고객명 검색..."
          className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 주문 등록 모달 */}
      <SalesOrderAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
